import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaCookieBite, FaTimes } from 'react-icons/fa';

const CONSENT_KEY = 'geriatriaserena_cookie_consent';

/**
 * Aplica el consentimiento guardado a Google Consent Mode v2 y al Pixel de Meta.
 * Se ejecuta tanto al cargar la página (si ya había una decisión previa)
 * como al hacer clic en Aceptar/Rechazar.
 */
const applyConsent = (granted) => {
  if (typeof window === 'undefined') return;

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: granted ? 'granted' : 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied',
      analytics_storage: granted ? 'granted' : 'denied'
    });
  }

  if (typeof window.fbq === 'function') {
    window.fbq('consent', granted ? 'grant' : 'revoke');
  }
};

function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = window.localStorage.getItem(CONSENT_KEY);
    if (stored === 'accepted') {
      applyConsent(true);
    } else if (stored === 'rejected') {
      applyConsent(false);
    } else {
      setVisible(true);
    }
  }, []);

  const handleChoice = useCallback((granted) => {
    window.localStorage.setItem(CONSENT_KEY, granted ? 'accepted' : 'rejected');
    applyConsent(granted);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de uso de cookies"
      className="fixed bottom-0 inset-x-0 z-[9999] p-4 sm:p-6"
    >
      <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 bg-blue-100 rounded-lg text-blue-600 shrink-0">
            <FaCookieBite className="w-5 h-5" />
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            Usamos cookies propias y de terceros (Google Analytics, Google Ads y Meta/Facebook) para
            analizar cómo navegas en nuestro sitio, medir el rendimiento de nuestras campañas y
            mostrarte contenido relevante, conforme a nuestro{' '}
            <Link to="/privacy-policy" className="text-blue-600 underline hover:text-blue-700">
              Aviso de Privacidad
            </Link>
            . Puedes aceptar todas las cookies o rechazar las que no sean estrictamente necesarias.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 self-stretch sm:self-auto">
          <button
            type="button"
            onClick={() => handleChoice(false)}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg font-semibold text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Rechazar
          </button>
          <button
            type="button"
            onClick={() => handleChoice(true)}
            className="flex-1 sm:flex-none px-5 py-2.5 rounded-lg font-semibold text-sm text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            Aceptar cookies
          </button>
          <button
            type="button"
            onClick={() => handleChoice(false)}
            aria-label="Cerrar y rechazar cookies no esenciales"
            className="hidden sm:inline-flex p-2 text-gray-400 hover:text-gray-600"
          >
            <FaTimes />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
